import { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';

/**
 * Custom hook for managing data fetching in Aircraft Lifecycle
 * Handles fetching aircraft, assemblies, components, and dropdown options
 */
export const useAircraftLifecycleData = (selectedAircraft, showArchived) => {
  const [aircraft, setAircraft] = useState([]);
  const [assemblies, setAssemblies] = useState([]);
  const [components, setComponents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dropdownOptions, setDropdownOptions] = useState({
    aircraft: [],
    ataChapters: [],
    assemblyTypes: [],
    manufacturerNames: [],
    models: [],
    licenses: [],
    parts: [],
    monitorTypes: []
  });
  
  // Ref to track if dropdown options are being fetched to prevent concurrent calls
  const fetchingDropdownsRef = useRef(false);

  useEffect(() => {
    fetchAircraft();
    fetchDropdownOptions();
  }, []);

  useEffect(() => {
    if (!selectedAircraft) return;
    const controller = new AbortController();
    const signal = controller.signal;
    fetchAssemblies(signal);
    fetchComponents(signal);
    return () => controller.abort();
  }, [selectedAircraft, showArchived]);

  // Listen for master data updates to refresh dropdowns
  useEffect(() => {
    const handleMasterDataUpdate = (event) => {
      // Refresh dropdown options when master data is updated
      fetchDropdownOptions();
    };

    window.addEventListener('masterDataUpdated', handleMasterDataUpdate);

    return () => {
      window.removeEventListener('masterDataUpdated', handleMasterDataUpdate);
    };
  }, []);

  const fetchAircraft = async () => {
    try {
      const response = await axios.get('/api/aircraft');
      setAircraft(response.data);
      setDropdownOptions(prev => ({ ...prev, aircraft: response.data }));
    } catch (error) {
      console.error('Error fetching aircraft:', error);
    }
  };

  const fetchDropdownOptions = useCallback(async () => {
    // Prevent concurrent calls
    if (fetchingDropdownsRef.current) {
      return;
    }
    
    fetchingDropdownsRef.current = true;
    try {
      const [ataChapters, assemblyTypes, manufacturerNames, models, licenses, parts, monitorTypes] = await Promise.all([
        axios.get('/api/master-data/ata-chapters', { params: { isActive: 'true' } }),
        axios.get('/api/master-data/assembly-types', { params: { isActive: 'true' } }),
        axios.get('/api/master-data/manufacturer-names', { params: { isActive: 'true' } }),
        axios.get('/api/master-data/models', { params: { isActive: 'true' } }),
        axios.get('/api/master-data/licenses', { params: { isActive: 'true' } }),
        axios.get('/api/parts', { params: { isActive: 'true', pageSize: 1000, page: 1 } }),
        axios.get('/api/master-data/monitor-types', { params: { isActive: 'true' } })
      ]);
      setDropdownOptions(prev => ({
        ...prev,
        ataChapters: ataChapters.data,
        assemblyTypes: assemblyTypes.data,
        manufacturerNames: manufacturerNames.data,
        models: models.data,
        licenses: licenses.data,
        parts: parts.data.parts || parts.data || [],
        monitorTypes: monitorTypes.data
      }));
    } catch (error) {
      console.error('Error fetching dropdown options:', error);
    } finally {
      fetchingDropdownsRef.current = false;
    }
  }, []);

  const fetchAssemblies = async (signal = null) => {
    if (!selectedAircraft) return;
    setLoading(true);
    try {
      const params = { aircraftId: selectedAircraft._id };
      if (!showArchived) {
        params.isActive = 'true';
      }
      const config = signal ? { params, signal } : { params };
      const response = await axios.get('/api/assemblies', config);
      if (!signal?.aborted) setAssemblies(response.data);
    } catch (error) {
      if ((axios.isCancel && axios.isCancel(error)) || error?.code === 'ERR_CANCELED') return;
      console.error('Error fetching assemblies:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchComponents = async (signal = null) => {
    if (!selectedAircraft) return;
    try {
      const params = { aircraftId: selectedAircraft._id };
      if (!showArchived) {
        params.isActive = 'true';
      }
      const config = signal ? { params, signal } : { params };
      const response = await axios.get('/api/components', config);
      if (!signal?.aborted) setComponents(response.data);
    } catch (error) {
      if (axios.isCancel && axios.isCancel(error)) return;
      console.error('Error fetching components:', error);
    }
  };

  return {
    aircraft,
    assemblies,
    components,
    loading,
    dropdownOptions,
    fetchAircraft,
    fetchAssemblies,
    fetchComponents,
    fetchDropdownOptions
  };
};
